import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_ITEMS } from "@/constants/nav";
import { LanguageSelector } from "@/components/LanguageSelector";
import { QuoteModal } from "@/components/QuoteModal";
import { Button } from "@/components/ui/button";
import rkgLogo from "@/assets/rkg-logo-160.png";

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);

  useEffect(() => {
    const closeOnRouteChange = () => setMobileOpen(false);
    window.addEventListener("popstate", closeOnRouteChange);
    return () => window.removeEventListener("popstate", closeOnRouteChange);
  }, []);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 w-full bg-white border-b border-[#0B4F37]/10 shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-20 flex items-center justify-between gap-6">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5 shrink-0">
          <img src={rkgLogo} alt="RKG NATURALS" className="h-14 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end
              data-testid={item.testId}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
                  isActive ? "text-[#0B4F37] bg-[#F2F9F5]" : "text-slate-600 hover:text-[#0B4F37] hover:bg-[#F2F9F5]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <LanguageSelector />
          <Button
            data-testid="request-quote-button"
            onClick={() => setQuoteOpen(true)}
            className="bg-[#0B4F37] hover:bg-[#042D1F] text-white font-semibold rounded-full px-5"
          >
            Request a Quote
          </Button>
        </div>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle navigation menu"
          onClick={() => setMobileOpen((v) => !v)}
          className="lg:hidden flex h-10 w-10 items-center justify-center rounded-full text-[#042D1F] hover:bg-[#F2F9F5]"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <div
        data-testid="mobile-menu-drawer"
        aria-hidden={!mobileOpen}
        className={`lg:hidden overflow-hidden border-t border-[#0B4F37]/10 bg-white transition-[max-height] duration-200 ${
          mobileOpen ? "max-h-[520px]" : "max-h-0 pointer-events-none"
        }`}
      >
        <nav className="flex flex-col px-6 py-4 gap-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end
              data-testid={`mobile-${item.testId}`}
              tabIndex={mobileOpen ? 0 : -1}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `px-3 py-2.5 rounded-lg text-sm font-medium ${
                  isActive ? "bg-[#F2F9F5] text-[#0B4F37]" : "text-slate-600"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <div className="flex items-center justify-between pt-3 mt-2 border-t border-[#E2E8F0]">
            <LanguageSelector testId="mobile-language-selector-dropdown" />
            <Button
              data-testid="mobile-request-quote-button"
              onClick={() => {
                setMobileOpen(false);
                setQuoteOpen(true);
              }}
              className="bg-[#0B4F37] hover:bg-[#042D1F] text-white font-semibold rounded-full px-5"
            >
              Request a Quote
            </Button>
          </div>
        </nav>
      </div>

      <QuoteModal open={quoteOpen} onOpenChange={setQuoteOpen} />
    </header>
  );
};
